import jsPDF from 'jspdf';
import type { BranchRecommendation } from './personalizedCollegeService';

import 'jspdf/dist/polyfills.es.js';

interface ReportData {
  studentInfo: {
    name: string;
    email: string;
    exam: string;
    rank: string;
    category: string;
    stream: string;
    states: string;
  };
  recommendations: {
    colleges: BranchRecommendation[];
    total: number;
  };
  paymentInfo: {
    amountPaid: number;
    promoCode: string | null;
    discount: number;
  };
}

const colors = {
  primary:   [30, 87, 153]   as [number, number, number],
  accent:    [0, 168, 142]   as [number, number, number],
  dark:      [22, 30, 46]    as [number, number, number],
  mid:       [60, 72, 95]    as [number, number, number],
  secondary: [100, 116, 139] as [number, number, number],
  light:     [245, 247, 250] as [number, number, number],
  white:     [255, 255, 255] as [number, number, number],
  border:    [218, 224, 234] as [number, number, number],
  tableHead: [235, 240, 248] as [number, number, number],
};

const loadImageAsBase64 = (src: string): Promise<string> =>
  new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = 'Anonymous';
    img.onload = () => {
      const canvas = document.createElement('canvas');
      canvas.width = img.width;
      canvas.height = img.height;
      const ctx = canvas.getContext('2d');
      if (!ctx) return reject(new Error('Canvas context unavailable'));
      ctx.drawImage(img, 0, 0);
      resolve(canvas.toDataURL('image/png'));
    };
    img.onerror = reject;
    img.src = src;
  });

export const generatePDF = async (data: ReportData): Promise<void> => {
  const pdf = new jsPDF('p', 'mm', 'a4');

  const pageWidth  = pdf.internal.pageSize.getWidth();
  const pageHeight = pdf.internal.pageSize.getHeight();
  const margin = 18;
  const contentWidth = pageWidth - margin * 2;

  let yPosition = 0;

  // Load logo
  let logoBase64: string | null = null;
  try {
    logoBase64 = await loadImageAsBase64('/Grad-Vikalp.png');
  } catch {
    logoBase64 = null;
  }

  const drawHeader = (sectionLabel = '') => {
    // Top bar
    pdf.setFillColor(...colors.primary);
    pdf.rect(0, 0, pageWidth, 20, 'F');

    // Accent strip
    pdf.setFillColor(...colors.accent);
    pdf.rect(0, 20, pageWidth, 2.5, 'F');

    // Logo
    if (logoBase64) {
      pdf.addImage(logoBase64, 'PNG', margin, 3, 28, 14);
    } else {
      pdf.setFillColor(...colors.white);
      pdf.roundedRect(margin, 3, 28, 14, 2, 2, 'F');
      pdf.setTextColor(...colors.primary);
      pdf.setFontSize(10);
      pdf.setFont('helvetica', 'bold');
      pdf.text('GRAD VIKALP', margin + 14, 12, { align: 'center' });
    }

    // Brand name
    pdf.setTextColor(...colors.white);
    pdf.setFontSize(11);
    pdf.setFont('helvetica', 'bold');
    pdf.text('Grad Vikalp', margin + 32, 10);
    pdf.setFontSize(7);
    pdf.setFont('helvetica', 'normal');
    pdf.text('Personalized College Recommendations', margin + 32, 15);

    // Section label right-aligned
    if (sectionLabel) {
      pdf.setFontSize(8);
      pdf.setFont('helvetica', 'bold');
      pdf.text(sectionLabel.toUpperCase(), pageWidth - margin, 12, { align: 'right' });
    }

    // Footer
    pdf.setFillColor(...colors.light);
    pdf.rect(0, pageHeight - 12, pageWidth, 12, 'F');
    pdf.setDrawColor(...colors.border);
    pdf.line(0, pageHeight - 12, pageWidth, pageHeight - 12);

    if (logoBase64) {
      pdf.addImage(logoBase64, 'PNG', margin, pageHeight - 11, 14, 7);
    }
    pdf.setTextColor(...colors.secondary);
    pdf.setFontSize(7);
    pdf.setFont('helvetica', 'normal');
    pdf.text('gradvikalp.com  |  Your College Guidance Partner', margin + (logoBase64 ? 16 : 0), pageHeight - 5.5);
    pdf.text(
      `Generated: ${new Date().toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}  |  Page ${pdf.getNumberOfPages()}`,
      pageWidth - margin, pageHeight - 5.5, { align: 'right' }
    );
  };

  const addNewPage = (label = '') => {
    pdf.addPage();
    drawHeader(label);
    return 30;
  };

  const checkPageBreak = (needed: number) => {
    if (yPosition + needed > pageHeight - 18) {
      yPosition = addNewPage('College Recommendations');
      return true;
    }
    return false;
  };

  // ─── COVER PAGE ────────────────────────────────────────────────────────────

  // Hero gradient block
  pdf.setFillColor(...colors.primary);
  pdf.rect(0, 0, pageWidth, 110, 'F');

  // Accent diagonal band
  pdf.setFillColor(...colors.accent);
  pdf.rect(0, 100, pageWidth, 4, 'F');

  // Large logo centred in hero
  if (logoBase64) {
    const logoW = 52;
    const logoH = 26;
    pdf.addImage(logoBase64, 'PNG', (pageWidth - logoW) / 2, 18, logoW, logoH);
  } else {
    pdf.setFillColor(...colors.white);
    pdf.roundedRect((pageWidth - 52) / 2, 18, 52, 26, 4, 4, 'F');
    pdf.setTextColor(...colors.primary);
    pdf.setFontSize(16);
    pdf.setFont('helvetica', 'bold');
    pdf.text('GRAD VIKALP', pageWidth / 2, 35, { align: 'center' });
  }

  pdf.setTextColor(...colors.white);
  pdf.setFontSize(22);
  pdf.setFont('helvetica', 'bold');
  pdf.text('Personalized College', pageWidth / 2, 60, { align: 'center' });
  pdf.text('Recommendation Report', pageWidth / 2, 72, { align: 'center' });

  pdf.setFontSize(10);
  pdf.setFont('helvetica', 'normal');
  pdf.text(`Prepared exclusively for ${data.studentInfo.name}`, pageWidth / 2, 84, { align: 'center' });

  pdf.setFontSize(9);
  pdf.text(
    `${data.studentInfo.exam}  |  Rank: ${data.studentInfo.rank}  |  Category: ${data.studentInfo.category}`,
    pageWidth / 2, 94, { align: 'center' }
  );

  // ── Student info card ──
  const cardY = 116;
  pdf.setFillColor(...colors.white);
  pdf.setDrawColor(...colors.border);
  pdf.roundedRect(margin, cardY, contentWidth, 72, 4, 4, 'FD');

  // Card title bar
  pdf.setFillColor(...colors.tableHead);
  pdf.roundedRect(margin, cardY, contentWidth, 11, 4, 4, 'F');
  pdf.rect(margin, cardY + 6, contentWidth, 5, 'F'); // square bottom corners
  pdf.setTextColor(...colors.primary);
  pdf.setFontSize(9);
  pdf.setFont('helvetica', 'bold');
  pdf.text('STUDENT PROFILE', margin + 6, cardY + 7.5);

  const leftX  = margin + 6;
  const rightX = margin + contentWidth / 2 + 4;
  const rowH   = 9;
  let  rowY    = cardY + 18;

  const infoRows: [string, string][] = [
    ['Full Name',        data.studentInfo.name],
    ['Email',            data.studentInfo.email],
    ['Examination',      data.studentInfo.exam],
    ['Rank',             data.studentInfo.rank],
    ['Category',         data.studentInfo.category],
    ['Stream',           data.studentInfo.stream],
    ['State Preferences', data.studentInfo.states],
  ];

  const leftRows  = infoRows.slice(0, 4);
  const rightRows = infoRows.slice(4, 7);

  leftRows.forEach(([label, value]) => {
    pdf.setFontSize(8);
    pdf.setFont('helvetica', 'bold');
    pdf.setTextColor(...colors.secondary);
    pdf.text(label, leftX, rowY);
    pdf.setFont('helvetica', 'normal');
    pdf.setTextColor(...colors.dark);
    pdf.text(String(value), leftX, rowY + 4.5);
    rowY += rowH;
  });

  rowY = cardY + 18;
  rightRows.forEach(([label, value]) => {
    pdf.setFontSize(8);
    pdf.setFont('helvetica', 'bold');
    pdf.setTextColor(...colors.secondary);
    pdf.text(label, rightX, rowY);
    pdf.setFont('helvetica', 'normal');
    pdf.setTextColor(...colors.dark);
    pdf.text(String(value), rightX, rowY + 4.5);
    rowY += rowH;
  });

  // ── Summary pill ──
  const pillY = cardY + 78;
  pdf.setFillColor(...colors.accent);
  pdf.roundedRect(margin, pillY, contentWidth, 14, 4, 4, 'F');
  pdf.setTextColor(...colors.white);
  pdf.setFontSize(10);
  pdf.setFont('helvetica', 'bold');
  pdf.text(
    `${data.recommendations.total} College Recommendations Generated`,
    pageWidth / 2, pillY + 9.5, { align: 'center' }
  );

  // ── Payment info ──
  const payY = pillY + 22;
  pdf.setFillColor(...colors.white);
  pdf.setDrawColor(...colors.border);
  pdf.roundedRect(margin, payY, contentWidth, 22, 3, 3, 'FD');

  pdf.setFillColor(...colors.tableHead);
  pdf.roundedRect(margin, payY, contentWidth, 9, 3, 3, 'F');
  pdf.rect(margin, payY + 5, contentWidth, 4, 'F');
  pdf.setTextColor(...colors.primary);
  pdf.setFontSize(8);
  pdf.setFont('helvetica', 'bold');
  pdf.text('PAYMENT DETAILS', margin + 6, payY + 6.5);

  pdf.setTextColor(...colors.dark);
  pdf.setFont('helvetica', 'normal');
  pdf.setFontSize(8.5);
  pdf.text(`Amount Paid: ₹${data.paymentInfo.amountPaid}`, margin + 6, payY + 16);
  if (data.paymentInfo.promoCode) {
    pdf.text(
      `Promo Code: ${data.paymentInfo.promoCode}  (₹${data.paymentInfo.discount} discount applied)`,
      margin + contentWidth / 2 + 4, payY + 16
    );
  }

  // ── Cover footer strip ──
  pdf.setFillColor(...colors.primary);
  pdf.rect(0, pageHeight - 20, pageWidth, 20, 'F');
  pdf.setFillColor(...colors.accent);
  pdf.rect(0, pageHeight - 20, pageWidth, 2.5, 'F');

  if (logoBase64) {
    pdf.addImage(logoBase64, 'PNG', margin, pageHeight - 16, 22, 11);
  }
  pdf.setTextColor(...colors.white);
  pdf.setFontSize(8);
  pdf.setFont('helvetica', 'normal');
  pdf.text('gradvikalp.com  |  Your College Guidance Partner', margin + (logoBase64 ? 25 : 0), pageHeight - 8);
  pdf.text(
    `Generated on ${new Date().toLocaleDateString('en-IN', { day: '2-digit', month: 'long', year: 'numeric' })}`,
    pageWidth - margin, pageHeight - 8, { align: 'right' }
  );

  // ─── COLLEGE PAGES ─────────────────────────────────────────────────────────

  yPosition = addNewPage('College Recommendations');

  // Section heading
  pdf.setFillColor(...colors.primary);
  pdf.roundedRect(margin, yPosition, contentWidth, 12, 3, 3, 'F');
  pdf.setTextColor(...colors.white);
  pdf.setFontSize(10);
  pdf.setFont('helvetica', 'bold');
  pdf.text(`ALL RECOMMENDATIONS  (${data.recommendations.total} colleges)`, margin + 6, yPosition + 8);
  yPosition += 18;

  data.recommendations.colleges.forEach((college, index) => {

    // ── College card top ──
    const estCardH = 60 + (college.all_rounds && college.all_rounds.length > 0 ? 12 + college.all_rounds.length * 7 + 8 : 0);
    checkPageBreak(estCardH);

    // Card outer border
    pdf.setFillColor(...colors.white);
    pdf.setDrawColor(...colors.border);
    pdf.roundedRect(margin, yPosition, contentWidth, 54, 3, 3, 'FD');

    // Colored left accent bar
    pdf.setFillColor(...colors.accent);
    pdf.rect(margin, yPosition, 4, 54, 'F');

    // Index badge
    pdf.setFillColor(...colors.primary);
    pdf.circle(margin + 11, yPosition + 8, 5, 'F');
    pdf.setTextColor(...colors.white);
    pdf.setFontSize(7);
    pdf.setFont('helvetica', 'bold');
    pdf.text(String(index + 1), margin + 11, yPosition + 10.5, { align: 'center' });

    // College name
    pdf.setTextColor(...colors.dark);
    pdf.setFontSize(11);
    pdf.setFont('helvetica', 'bold');
    const maxNameW = contentWidth - 30;
    const nameLines = pdf.splitTextToSize(college.college.name, maxNameW);
    pdf.text(nameLines, margin + 20, yPosition + 7);

    // Branch + code
    pdf.setTextColor(...colors.accent);
    pdf.setFontSize(9);
    pdf.setFont('helvetica', 'bold');
    pdf.text(
      `${college.branch_name}${college.branch_code ? '  (' + college.branch_code + ')' : ''}`,
      margin + 20, yPosition + 15
    );

    // Divider
    pdf.setDrawColor(...colors.border);
    pdf.line(margin + 4, yPosition + 19, margin + contentWidth - 2, yPosition + 19);

    // Detail grid (2 columns × 3 rows)
    const dLeft  = margin + 8;
    const dRight = margin + contentWidth / 2 + 4;
    const dRowH  = 8.5;
    let  dY      = yPosition + 25;

    const detailsLeft: [string, string][] = [
      ['Location',    `${college.college.location}, ${college.college.state}`],
      ['College Type', college.college.college_type.replace(/_/g, ' ')],
      ['Degree',      `${college.degree_type || ''}  ${college.duration ? '(' + college.duration + ')' : ''}`],
    ];
    const detailsRight: [string, string][] = [
      ['Annual Fees',   college.college.fees_annual || 'N/A'],
      ['Total Seats',   String(college.college.seats_total || college.seats_available || 'N/A')],
      [`Expected Cutoff (${data.studentInfo.category})`,
        String((college[`cutoff_${data.studentInfo.category.toLowerCase()}` as keyof typeof college]) || 'N/A')],
    ];

    const drawDetail = (label: string, value: string, x: number, y: number) => {
      pdf.setFontSize(7.5);
      pdf.setFont('helvetica', 'bold');
      pdf.setTextColor(...colors.secondary);
      pdf.text(label, x, y);
      pdf.setFont('helvetica', 'normal');
      pdf.setTextColor(...colors.dark);
      const val = pdf.splitTextToSize(value, contentWidth / 2 - 16);
      pdf.text(val, x, y + 4);
    };

    detailsLeft.forEach(([l, v], i) => drawDetail(l, v, dLeft, dY + i * dRowH));
    detailsRight.forEach(([l, v], i) => drawDetail(l, v, dRight, dY + i * dRowH));

    yPosition += 57;

    // ── Counseling rounds table ──
    if (college.all_rounds && college.all_rounds.length > 0) {
      checkPageBreak(12 + college.all_rounds.length * 7 + 6);

      // Table header bar
      pdf.setFillColor(...colors.tableHead);
      pdf.setDrawColor(...colors.border);
      pdf.rect(margin, yPosition, contentWidth, 9, 'FD');
      pdf.setFillColor(...colors.primary);
      pdf.rect(margin, yPosition, 4, 9, 'F');
      pdf.setTextColor(...colors.primary);
      pdf.setFontSize(7.5);
      pdf.setFont('helvetica', 'bold');
      pdf.text('COUNSELING ROUNDS — HISTORICAL CUTOFF DATA', margin + 8, yPosition + 6);
      yPosition += 9;

      // Column headers
      const cols = [
        { label: 'Round',    x: margin + 4,   w: 44 },
        { label: 'Year',     x: margin + 48,  w: 18 },
        { label: 'General',  x: margin + 66,  w: 24 },
        { label: 'OBC',      x: margin + 90,  w: 20 },
        { label: 'SC',       x: margin + 110, w: 20 },
        { label: 'ST',       x: margin + 130, w: 20 },
        { label: 'EWS',      x: margin + 150, w: 20 },
      ];

      pdf.setFillColor(220, 230, 245);
      pdf.rect(margin, yPosition, contentWidth, 7, 'F');

      pdf.setTextColor(...colors.primary);
      pdf.setFontSize(6.5);
      pdf.setFont('helvetica', 'bold');
      cols.forEach(c => pdf.text(c.label, c.x, yPosition + 4.8));
      yPosition += 7;

      // Round rows
      college.all_rounds.forEach((round, ri) => {
        checkPageBreak(8);

        if (ri % 2 === 0) {
          pdf.setFillColor(250, 252, 255);
        } else {
          pdf.setFillColor(...colors.white);
        }
        pdf.rect(margin, yPosition, contentWidth, 7, 'F');
        pdf.setDrawColor(...colors.border);
        pdf.line(margin, yPosition + 7, margin + contentWidth, yPosition + 7);

        pdf.setTextColor(...colors.dark);
        pdf.setFontSize(6.5);
        pdf.setFont('helvetica', 'normal');

        const rowVals = [
          round.round_name,
          String(round.year),
          round.cutoff_general != null ? String(round.cutoff_general) : '—',
          round.cutoff_obc      != null ? String(round.cutoff_obc)     : '—',
          round.cutoff_sc       != null ? String(round.cutoff_sc)      : '—',
          round.cutoff_st       != null ? String(round.cutoff_st)      : '—',
          round.cutoff_ews      != null ? String(round.cutoff_ews)     : '—',
        ];

        cols.forEach((c, ci) => {
          const val = pdf.splitTextToSize(rowVals[ci], c.w - 2);
          pdf.text(val, c.x, yPosition + 4.8);
        });

        yPosition += 7;
      });

      // Bottom border of table
      pdf.setDrawColor(...colors.border);
      pdf.line(margin, yPosition, margin + contentWidth, yPosition);
      yPosition += 4;
    }

    yPosition += 6;
  });

  // ─── DISCLAIMER PAGE ───────────────────────────────────────────────────────

  yPosition = addNewPage('Disclaimer');

  pdf.setFillColor(...colors.primary);
  pdf.roundedRect(margin, yPosition, contentWidth, 12, 3, 3, 'F');
  pdf.setTextColor(...colors.white);
  pdf.setFontSize(10);
  pdf.setFont('helvetica', 'bold');
  pdf.text('IMPORTANT DISCLAIMER', margin + 6, yPosition + 8);
  yPosition += 18;

  const disclaimerText = [
    'This report is based on historical counseling data and trends. Final admission depends on real-time cutoffs, seat availability, and official counseling rounds conducted by the respective authorities.',
    '',
    'The cutoff data provided for each counseling round reflects previous years\' actual closing ranks. These are for reference only and are not a guarantee of admission.',
    '',
    'We strongly recommend consulting the official counseling portals and college admission offices for the most current information before making your final decisions.',
    '',
    'Grad Vikalp is a guidance platform. Responsibility for final college and course selection lies with the student and their guardians.',
  ];

  disclaimerText.forEach(line => {
    if (line === '') {
      yPosition += 5;
    } else {
      checkPageBreak(14);
      pdf.setFontSize(9);
      pdf.setFont('helvetica', 'normal');
      pdf.setTextColor(...colors.mid);
      const wrapped = pdf.splitTextToSize(line, contentWidth - 10);
      pdf.text(wrapped, margin + 5, yPosition);
      yPosition += wrapped.length * 5.5;
    }
  });

  yPosition += 10;

  // Signature block
  pdf.setFillColor(...colors.light);
  pdf.roundedRect(margin, yPosition, contentWidth, 28, 3, 3, 'F');
  pdf.setDrawColor(...colors.border);
  pdf.roundedRect(margin, yPosition, contentWidth, 28, 3, 3, 'D');

  if (logoBase64) {
    pdf.addImage(logoBase64, 'PNG', margin + 6, yPosition + 5, 26, 13);
  }
  const sigX = logoBase64 ? margin + 36 : margin + 10;
  pdf.setTextColor(...colors.dark);
  pdf.setFontSize(10);
  pdf.setFont('helvetica', 'bold');
  pdf.text('Grad Vikalp Team', sigX, yPosition + 11);
  pdf.setFontSize(8);
  pdf.setFont('helvetica', 'normal');
  pdf.setTextColor(...colors.secondary);
  pdf.text('Your College Guidance Partner', sigX, yPosition + 18);
  pdf.text('gradvikalp.com', sigX, yPosition + 24);

  // Save
  const fileName = `${data.studentInfo.name.replace(/\s+/g, '_')}_College_Recommendations_${data.studentInfo.exam.replace(/\s+/g, '_')}.pdf`;
  pdf.save(fileName);
};

export const generatePDFBase64 = async (data: ReportData): Promise<{ base64: string; fileName: string }> => {
  const pdf = new jsPDF('p', 'mm', 'a4');

  const pageWidth  = pdf.internal.pageSize.getWidth();
  const pageHeight = pdf.internal.pageSize.getHeight();
  const margin = 18;
  const contentWidth = pageWidth - margin * 2;

  let yPosition = 0;

  let logoBase64: string | null = null;
  try {
    logoBase64 = await loadImageAsBase64('/Grad-Vikalp.png');
  } catch {
    logoBase64 = null;
  }

  const drawHeader = (sectionLabel = '') => {
    pdf.setFillColor(...colors.primary);
    pdf.rect(0, 0, pageWidth, 20, 'F');
    pdf.setFillColor(...colors.accent);
    pdf.rect(0, 20, pageWidth, 2.5, 'F');
    if (logoBase64) {
      pdf.addImage(logoBase64, 'PNG', margin, 3, 28, 14);
    } else {
      pdf.setFillColor(...colors.white);
      pdf.roundedRect(margin, 3, 28, 14, 2, 2, 'F');
      pdf.setTextColor(...colors.primary);
      pdf.setFontSize(10);
      pdf.setFont('helvetica', 'bold');
      pdf.text('GRAD VIKALP', margin + 14, 12, { align: 'center' });
    }
    pdf.setTextColor(...colors.white);
    pdf.setFontSize(11);
    pdf.setFont('helvetica', 'bold');
    pdf.text('Grad Vikalp', margin + 32, 10);
    pdf.setFontSize(7);
    pdf.setFont('helvetica', 'normal');
    pdf.text('Personalized College Recommendations', margin + 32, 15);
    if (sectionLabel) {
      pdf.setFontSize(8);
      pdf.setFont('helvetica', 'bold');
      pdf.text(sectionLabel.toUpperCase(), pageWidth - margin, 12, { align: 'right' });
    }
    pdf.setFillColor(...colors.light);
    pdf.rect(0, pageHeight - 12, pageWidth, 12, 'F');
    pdf.setDrawColor(...colors.border);
    pdf.line(0, pageHeight - 12, pageWidth, pageHeight - 12);
    if (logoBase64) {
      pdf.addImage(logoBase64, 'PNG', margin, pageHeight - 11, 14, 7);
    }
    pdf.setTextColor(...colors.secondary);
    pdf.setFontSize(7);
    pdf.setFont('helvetica', 'normal');
    pdf.text('gradvikalp.com  |  Your College Guidance Partner', margin + (logoBase64 ? 16 : 0), pageHeight - 5.5);
    pdf.text(
      `Generated: ${new Date().toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}  |  Page ${pdf.getNumberOfPages()}`,
      pageWidth - margin, pageHeight - 5.5, { align: 'right' }
    );
  };

  const addNewPage = (label = '') => {
    pdf.addPage();
    drawHeader(label);
    return 30;
  };

  const checkPageBreak = (needed: number) => {
    if (yPosition + needed > pageHeight - 18) {
      yPosition = addNewPage('College Recommendations');
      return true;
    }
    return false;
  };

  pdf.setFillColor(...colors.primary);
  pdf.rect(0, 0, pageWidth, 110, 'F');
  pdf.setFillColor(...colors.accent);
  pdf.rect(0, 100, pageWidth, 4, 'F');
  if (logoBase64) {
    pdf.addImage(logoBase64, 'PNG', (pageWidth - 52) / 2, 18, 52, 26);
  } else {
    pdf.setFillColor(...colors.white);
    pdf.roundedRect((pageWidth - 52) / 2, 18, 52, 26, 4, 4, 'F');
    pdf.setTextColor(...colors.primary);
    pdf.setFontSize(16);
    pdf.setFont('helvetica', 'bold');
    pdf.text('GRAD VIKALP', pageWidth / 2, 35, { align: 'center' });
  }
  pdf.setTextColor(...colors.white);
  pdf.setFontSize(22);
  pdf.setFont('helvetica', 'bold');
  pdf.text('Personalized College', pageWidth / 2, 60, { align: 'center' });
  pdf.text('Recommendation Report', pageWidth / 2, 72, { align: 'center' });
  pdf.setFontSize(10);
  pdf.setFont('helvetica', 'normal');
  pdf.text(`Prepared exclusively for ${data.studentInfo.name}`, pageWidth / 2, 84, { align: 'center' });
  pdf.setFontSize(9);
  pdf.text(
    `${data.studentInfo.exam}  |  Rank: ${data.studentInfo.rank}  |  Category: ${data.studentInfo.category}`,
    pageWidth / 2, 94, { align: 'center' }
  );

  const cardY = 116;
  pdf.setFillColor(...colors.white);
  pdf.setDrawColor(...colors.border);
  pdf.roundedRect(margin, cardY, contentWidth, 72, 4, 4, 'FD');
  pdf.setFillColor(...colors.tableHead);
  pdf.roundedRect(margin, cardY, contentWidth, 11, 4, 4, 'F');
  pdf.rect(margin, cardY + 6, contentWidth, 5, 'F');
  pdf.setTextColor(...colors.primary);
  pdf.setFontSize(9);
  pdf.setFont('helvetica', 'bold');
  pdf.text('STUDENT PROFILE', margin + 6, cardY + 7.5);

  const leftX  = margin + 6;
  const rightX = margin + contentWidth / 2 + 4;
  const rowH   = 9;
  let   rowY   = cardY + 18;

  const infoRows: [string, string][] = [
    ['Full Name',        data.studentInfo.name],
    ['Email',            data.studentInfo.email],
    ['Examination',      data.studentInfo.exam],
    ['Rank',             data.studentInfo.rank],
    ['Category',         data.studentInfo.category],
    ['Stream',           data.studentInfo.stream],
    ['State Preferences', data.studentInfo.states],
  ];

  infoRows.slice(0, 4).forEach(([label, value]) => {
    pdf.setFontSize(8); pdf.setFont('helvetica', 'bold'); pdf.setTextColor(...colors.secondary);
    pdf.text(label, leftX, rowY);
    pdf.setFont('helvetica', 'normal'); pdf.setTextColor(...colors.dark);
    pdf.text(String(value), leftX, rowY + 4.5);
    rowY += rowH;
  });

  rowY = cardY + 18;
  infoRows.slice(4, 7).forEach(([label, value]) => {
    pdf.setFontSize(8); pdf.setFont('helvetica', 'bold'); pdf.setTextColor(...colors.secondary);
    pdf.text(label, rightX, rowY);
    pdf.setFont('helvetica', 'normal'); pdf.setTextColor(...colors.dark);
    pdf.text(String(value), rightX, rowY + 4.5);
    rowY += rowH;
  });

  const pillY = cardY + 78;
  pdf.setFillColor(...colors.accent);
  pdf.roundedRect(margin, pillY, contentWidth, 14, 4, 4, 'F');
  pdf.setTextColor(...colors.white);
  pdf.setFontSize(10); pdf.setFont('helvetica', 'bold');
  pdf.text(`${data.recommendations.total} College Recommendations Generated`, pageWidth / 2, pillY + 9.5, { align: 'center' });

  const payY = pillY + 22;
  pdf.setFillColor(...colors.white); pdf.setDrawColor(...colors.border);
  pdf.roundedRect(margin, payY, contentWidth, 22, 3, 3, 'FD');
  pdf.setFillColor(...colors.tableHead);
  pdf.roundedRect(margin, payY, contentWidth, 9, 3, 3, 'F');
  pdf.rect(margin, payY + 5, contentWidth, 4, 'F');
  pdf.setTextColor(...colors.primary); pdf.setFontSize(8); pdf.setFont('helvetica', 'bold');
  pdf.text('PAYMENT DETAILS', margin + 6, payY + 6.5);
  pdf.setTextColor(...colors.dark); pdf.setFont('helvetica', 'normal'); pdf.setFontSize(8.5);
  pdf.text(`Amount Paid: ₹${data.paymentInfo.amountPaid}`, margin + 6, payY + 16);
  if (data.paymentInfo.promoCode) {
    pdf.text(`Promo Code: ${data.paymentInfo.promoCode}  (₹${data.paymentInfo.discount} discount applied)`, margin + contentWidth / 2 + 4, payY + 16);
  }

  pdf.setFillColor(...colors.primary);
  pdf.rect(0, pageHeight - 20, pageWidth, 20, 'F');
  pdf.setFillColor(...colors.accent);
  pdf.rect(0, pageHeight - 20, pageWidth, 2.5, 'F');
  if (logoBase64) {
    pdf.addImage(logoBase64, 'PNG', margin, pageHeight - 16, 22, 11);
  }
  pdf.setTextColor(...colors.white); pdf.setFontSize(8); pdf.setFont('helvetica', 'normal');
  pdf.text('gradvikalp.com  |  Your College Guidance Partner', margin + (logoBase64 ? 25 : 0), pageHeight - 8);
  pdf.text(
    `Generated on ${new Date().toLocaleDateString('en-IN', { day: '2-digit', month: 'long', year: 'numeric' })}`,
    pageWidth - margin, pageHeight - 8, { align: 'right' }
  );

  yPosition = addNewPage('College Recommendations');

  pdf.setFillColor(...colors.primary);
  pdf.roundedRect(margin, yPosition, contentWidth, 12, 3, 3, 'F');
  pdf.setTextColor(...colors.white); pdf.setFontSize(10); pdf.setFont('helvetica', 'bold');
  pdf.text(`ALL RECOMMENDATIONS  (${data.recommendations.total} colleges)`, margin + 6, yPosition + 8);
  yPosition += 18;

  data.recommendations.colleges.forEach((college, index) => {
    const estCardH = 60 + (college.all_rounds && college.all_rounds.length > 0 ? 12 + college.all_rounds.length * 7 + 8 : 0);
    checkPageBreak(estCardH);

    pdf.setFillColor(...colors.white); pdf.setDrawColor(...colors.border);
    pdf.roundedRect(margin, yPosition, contentWidth, 54, 3, 3, 'FD');
    pdf.setFillColor(...colors.accent);
    pdf.rect(margin, yPosition, 4, 54, 'F');
    pdf.setFillColor(...colors.primary);
    pdf.circle(margin + 11, yPosition + 8, 5, 'F');
    pdf.setTextColor(...colors.white); pdf.setFontSize(7); pdf.setFont('helvetica', 'bold');
    pdf.text(String(index + 1), margin + 11, yPosition + 10.5, { align: 'center' });

    pdf.setTextColor(...colors.dark); pdf.setFontSize(11); pdf.setFont('helvetica', 'bold');
    const nameLines = pdf.splitTextToSize(college.college.name, contentWidth - 30);
    pdf.text(nameLines, margin + 20, yPosition + 7);

    pdf.setTextColor(...colors.accent); pdf.setFontSize(9); pdf.setFont('helvetica', 'bold');
    pdf.text(`${college.branch_name}${college.branch_code ? '  (' + college.branch_code + ')' : ''}`, margin + 20, yPosition + 15);

    pdf.setDrawColor(...colors.border);
    pdf.line(margin + 4, yPosition + 19, margin + contentWidth - 2, yPosition + 19);

    const dLeft  = margin + 8;
    const dRight = margin + contentWidth / 2 + 4;
    const dRowH  = 8.5;
    const dY     = yPosition + 25;

    const detailsLeft: [string, string][] = [
      ['Location',     `${college.college.location}, ${college.college.state}`],
      ['College Type', college.college.college_type.replace(/_/g, ' ')],
      ['Degree',       `${college.degree_type || ''}  ${college.duration ? '(' + college.duration + ')' : ''}`],
    ];
    const detailsRight: [string, string][] = [
      ['Annual Fees',  college.college.fees_annual || 'N/A'],
      ['Total Seats',  String(college.college.seats_total || college.seats_available || 'N/A')],
      [`Expected Cutoff (${data.studentInfo.category})`,
        String((college[`cutoff_${data.studentInfo.category.toLowerCase()}` as keyof typeof college]) || 'N/A')],
    ];

    const drawDetail = (label: string, value: string, x: number, y: number) => {
      pdf.setFontSize(7.5); pdf.setFont('helvetica', 'bold'); pdf.setTextColor(...colors.secondary);
      pdf.text(label, x, y);
      pdf.setFont('helvetica', 'normal'); pdf.setTextColor(...colors.dark);
      pdf.text(pdf.splitTextToSize(value, contentWidth / 2 - 16), x, y + 4);
    };

    detailsLeft.forEach(([l, v], i) => drawDetail(l, v, dLeft, dY + i * dRowH));
    detailsRight.forEach(([l, v], i) => drawDetail(l, v, dRight, dY + i * dRowH));

    yPosition += 57;

    if (college.all_rounds && college.all_rounds.length > 0) {
      checkPageBreak(12 + college.all_rounds.length * 7 + 6);

      pdf.setFillColor(...colors.tableHead); pdf.setDrawColor(...colors.border);
      pdf.rect(margin, yPosition, contentWidth, 9, 'FD');
      pdf.setFillColor(...colors.primary);
      pdf.rect(margin, yPosition, 4, 9, 'F');
      pdf.setTextColor(...colors.primary); pdf.setFontSize(7.5); pdf.setFont('helvetica', 'bold');
      pdf.text('COUNSELING ROUNDS — HISTORICAL CUTOFF DATA', margin + 8, yPosition + 6);
      yPosition += 9;

      const cols = [
        { label: 'Round',   x: margin + 4,   w: 44 },
        { label: 'Year',    x: margin + 48,  w: 18 },
        { label: 'General', x: margin + 66,  w: 24 },
        { label: 'OBC',     x: margin + 90,  w: 20 },
        { label: 'SC',      x: margin + 110, w: 20 },
        { label: 'ST',      x: margin + 130, w: 20 },
        { label: 'EWS',     x: margin + 150, w: 20 },
      ];

      pdf.setFillColor(220, 230, 245);
      pdf.rect(margin, yPosition, contentWidth, 7, 'F');
      pdf.setTextColor(...colors.primary); pdf.setFontSize(6.5); pdf.setFont('helvetica', 'bold');
      cols.forEach(c => pdf.text(c.label, c.x, yPosition + 4.8));
      yPosition += 7;

      college.all_rounds.forEach((round, ri) => {
        checkPageBreak(8);
        pdf.setFillColor(ri % 2 === 0 ? 250 : 255, ri % 2 === 0 ? 252 : 255, 255);
        pdf.rect(margin, yPosition, contentWidth, 7, 'F');
        pdf.setDrawColor(...colors.border);
        pdf.line(margin, yPosition + 7, margin + contentWidth, yPosition + 7);
        pdf.setTextColor(...colors.dark); pdf.setFontSize(6.5); pdf.setFont('helvetica', 'normal');
        const rowVals = [
          round.round_name, String(round.year),
          round.cutoff_general != null ? String(round.cutoff_general) : '—',
          round.cutoff_obc     != null ? String(round.cutoff_obc)     : '—',
          round.cutoff_sc      != null ? String(round.cutoff_sc)      : '—',
          round.cutoff_st      != null ? String(round.cutoff_st)      : '—',
          round.cutoff_ews     != null ? String(round.cutoff_ews)     : '—',
        ];
        cols.forEach((c, ci) => pdf.text(pdf.splitTextToSize(rowVals[ci], c.w - 2), c.x, yPosition + 4.8));
        yPosition += 7;
      });

      pdf.setDrawColor(...colors.border);
      pdf.line(margin, yPosition, margin + contentWidth, yPosition);
      yPosition += 4;
    }

    yPosition += 6;
  });

  yPosition = addNewPage('Disclaimer');

  pdf.setFillColor(...colors.primary);
  pdf.roundedRect(margin, yPosition, contentWidth, 12, 3, 3, 'F');
  pdf.setTextColor(...colors.white); pdf.setFontSize(10); pdf.setFont('helvetica', 'bold');
  pdf.text('IMPORTANT DISCLAIMER', margin + 6, yPosition + 8);
  yPosition += 18;

  const disclaimerText = [
    'This report is based on historical counseling data and trends. Final admission depends on real-time cutoffs, seat availability, and official counseling rounds conducted by the respective authorities.',
    '',
    'The cutoff data provided for each counseling round reflects previous years\' actual closing ranks. These are for reference only and are not a guarantee of admission.',
    '',
    'We strongly recommend consulting the official counseling portals and college admission offices for the most current information before making your final decisions.',
    '',
    'Grad Vikalp is a guidance platform. Responsibility for final college and course selection lies with the student and their guardians.',
  ];

  disclaimerText.forEach(line => {
    if (line === '') {
      yPosition += 5;
    } else {
      checkPageBreak(14);
      pdf.setFontSize(9); pdf.setFont('helvetica', 'normal'); pdf.setTextColor(...colors.mid);
      const wrapped = pdf.splitTextToSize(line, contentWidth - 10);
      pdf.text(wrapped, margin + 5, yPosition);
      yPosition += wrapped.length * 5.5;
    }
  });

  yPosition += 10;

  pdf.setFillColor(...colors.light);
  pdf.roundedRect(margin, yPosition, contentWidth, 28, 3, 3, 'F');
  pdf.setDrawColor(...colors.border);
  pdf.roundedRect(margin, yPosition, contentWidth, 28, 3, 3, 'D');
  if (logoBase64) {
    pdf.addImage(logoBase64, 'PNG', margin + 6, yPosition + 5, 26, 13);
  }
  const sigX = logoBase64 ? margin + 36 : margin + 10;
  pdf.setTextColor(...colors.dark); pdf.setFontSize(10); pdf.setFont('helvetica', 'bold');
  pdf.text('Grad Vikalp Team', sigX, yPosition + 11);
  pdf.setFontSize(8); pdf.setFont('helvetica', 'normal'); pdf.setTextColor(...colors.secondary);
  pdf.text('Your College Guidance Partner', sigX, yPosition + 18);
  pdf.text('gradvikalp.com', sigX, yPosition + 24);

  const fileName = `${data.studentInfo.name.replace(/\s+/g, '_')}_College_Recommendations_${data.studentInfo.exam.replace(/\s+/g, '_')}.pdf`;
  const base64 = pdf.output('datauristring').split(',')[1];
  return { base64, fileName };
};
